import { useRef, useState, useEffect, useCallback } from 'react'
import Hls from 'hls.js'
import { VideoSources } from '../../types/video'
import { initHLSOnVideo } from '../../utils/videoUtils'
import VideoControls from './VideoControls'

interface VideoModalProps {
  sources: VideoSources | null
  isOpen: boolean
  onClose: () => void
  startTime?: number
}

const VideoModal = ({ sources, isOpen, onClose, startTime = 0 }: VideoModalProps) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  const togglePlay = useCallback(() => {
    const video = videoRef.current
    if (!video) return

    if (video.paused) {
      video.play().catch((e) => console.debug('Video play prevented:', e))
      setIsPlaying(true)
    } else {
      video.pause()
      setIsPlaying(false)
    }
  }, [])

  const handleSeek = useCallback((percent: number) => {
    const video = videoRef.current
    if (!video || !duration) return

    const newTime = (percent / 100) * duration
    video.currentTime = newTime
    setCurrentTime(newTime)
  }, [duration])

  const handleClose = useCallback(() => {
    videoRef.current?.pause()
    setIsPlaying(false)
    onClose()
  }, [onClose])

  // Initialize HLS when modal opens
  useEffect(() => {
    if (!isOpen || !videoRef.current || !sources) return

    setIsLoading(true)

    if (hlsRef.current) {
      hlsRef.current.destroy()
      hlsRef.current = null
    }

    const video = videoRef.current
    hlsRef.current = initHLSOnVideo(video, sources)

    video.currentTime = startTime
    setCurrentTime(startTime)
    video.play().catch(() => setIsPlaying(false))
    setIsPlaying(true)

    return () => {
      hlsRef.current?.destroy()
      hlsRef.current = null
    }
  }, [isOpen, sources, startTime])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentTime(0)
      setDuration(0)
      setIsPlaying(false)
      setIsLoading(true)
    }
  }, [isOpen])

  // Keyboard controls - FIXED: togglePlay in deps
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return
      if (e.key === 'Escape') handleClose()
      if (e.key === ' ') {
        e.preventDefault()
        togglePlay()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleClose, togglePlay])

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    setCurrentTime(video.currentTime)
  }, [])

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    setDuration(video.duration)
  }, [])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-[400px] mx-4 aspect-[9/16] max-h-[90dvh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative w-full h-full rounded-2xl overflow-hidden bg-black">
          <button
            onClick={handleClose}
            className="absolute top-5 right-5 z-30 w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center bg-[#4A4A4AB2] rounded-[6px] hover:bg-[#4A4A4A] transition-colors"
          >
            <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" viewBox="0 0 14 14" fill="none">
              <path d="M1 1L13 13M1 13L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>

          <video
            ref={videoRef}
            poster={sources?.poster}
            className="w-full h-full object-contain"
            playsInline
            preload="none"
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={() => setIsPlaying(false)}
            onWaiting={() => setIsLoading(true)}
            onCanPlay={() => setIsLoading(false)}
          />

          <VideoControls
            variant="modal"
            isPlaying={isPlaying}
            isLoading={isLoading}
            progress={progress}
            currentTime={currentTime}
            duration={duration}
            onTogglePlay={togglePlay}
            onSeek={handleSeek}
          />
        </div>
      </div>
    </div>
  )
}

export default VideoModal
